import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";

const HomePage = () => {
  const [slides, setSlides] = useState([]);
  const [services, setServices] = useState([]);
  const [welcome, setWelcome] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderReference = slides[0] || {};

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [slidesRes, servicesRes, welcomeRes, contactRes] = await Promise.all([
          axios.get(`${API}/slides`),
          axios.get(`${API}/services`),
          axios.get(`${API}/welcome`),
          axios.get(`${API}/contact-info`),
        ]);
        setSlides(slidesRes.data);
        setServices([...(servicesRes.data || [])].sort((a, b) => (a.order || 0) - (b.order || 0)).slice(0, 3));
        setWelcome(welcomeRes.data);
        setContactInfo(contactRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Auto-slide
  useEffect(() => {
    if (slides.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [slides.length]);

  return (
    <div data-testid="home-page">
      {/* Hero Slider */}
      <section className="hero-slider" data-testid="hero-slider">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`hero-slide ${index === currentSlide ? "active" : ""}`}
          >
            <img src={slide.image_url} alt={slide.title} />
            <div className="hero-overlay" />
            <div className="absolute inset-0 flex items-center z-20">
              <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 w-full">
                <div className="max-w-2xl animate-fade-in-up">
                  <h1 
                    className={`font-bold text-white mb-4 leading-tight ${sliderReference.title_size || slide.title_size || 'text-3xl md:text-4xl lg:text-5xl'}`}
                    style={{ 
                      color: slide.title_color || '#FFFFFF',
                      fontFamily: sliderReference.title_font || slide.title_font || 'Barlow'
                    }}
                  >
                    {slide.title}
                  </h1>
                  <p 
                    className={`mb-8 ${sliderReference.subtitle_size || slide.subtitle_size || 'text-lg md:text-xl'}`}
                    style={{ 
                      color: slide.subtitle_color || 'rgba(255,255,255,0.9)',
                      fontFamily: sliderReference.subtitle_font || slide.subtitle_font || 'Manrope'
                    }}
                  >
                    {slide.subtitle}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Slider Navigation Dots */}
        {slides.length > 1 && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-3 z-30">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide ? "bg-[#D32F2F] w-8" : "bg-white/50 hover:bg-white"
                }`}
                data-testid={`slider-dot-${index}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Services Preview */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2A43] mb-4 font-barlow">
              Unsere Leistungen
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Als Betonbau Meisterbetrieb bieten wir Ihnen ein umfassendes Leistungsspektrum für Ihr Bauvorhaben.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div key={service.id} className="service-card bg-white rounded-sm overflow-hidden shadow-md" data-testid={`service-card-${service.slug}`}>
                <div className="aspect-video overflow-hidden">
                  <img
                    src={service.image_url}
                    alt={service.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 
                    className={`font-bold mb-3 ${service.name_size || 'text-xl'}`}
                    style={{ 
                      color: service.name_color || '#0A2A43',
                      fontFamily: service.name_font || 'Barlow'
                    }}
                  >
                    {service.name}
                  </h3>
                  <p 
                    className={`mb-4 ${service.description_size || 'text-sm'}`}
                    style={{ 
                      color: service.description_color || '#475569',
                      fontFamily: service.description_font || 'Manrope'
                    }}
                  >
                    {service.short_description}
                  </p>
                  <Link
                    to="/leistungen"
                    className="inline-flex items-center text-[#D32F2F] font-semibold text-sm hover:gap-2 transition-all"
                  >
                    Mehr erfahren
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/leistungen" className="btn-primary" data-testid="all-services-link">
              Alle Leistungen ansehen
            </Link>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      {welcome && (
        <section className="section-padding bg-[#F2F2F2]" data-testid="welcome-section">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-[#D32F2F] font-semibold text-sm uppercase tracking-wider mb-2 block">
                  Betonbau Meisterbetrieb
                </span>
                <h2 
                  className={`font-bold mb-6 ${welcome.title_size || 'text-3xl md:text-4xl'}`}
                  style={{ 
                    color: welcome.title_color || '#0A2A43',
                    fontFamily: welcome.title_font || 'Barlow'
                  }}
                >
                  {welcome.title}
                </h2>
                <p 
                  className={`leading-relaxed mb-6 ${welcome.content_size || 'text-base'}`}
                  style={{ 
                    color: welcome.content_color || '#475569',
                    fontFamily: welcome.content_font || 'Manrope'
                  }}
                >
                  {welcome.content}
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#0A2A43] rounded-sm flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-[#0A2A43]">Meisterqualität</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#0A2A43] rounded-sm flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-[#0A2A43]">Termintreue</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#0A2A43] rounded-sm flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-[#0A2A43]">Erfahrung</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#0A2A43] rounded-sm flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-[#0A2A43]">Zuverlässig</span>
                  </div>
                </div>
                <Link to="/ueber-uns" className="btn-primary" data-testid="about-link">
                  Mehr über uns
                </Link>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1632862378103-8248dccb7e3d"
                  alt="Bauarbeiter bei der Planung"
                  className="rounded-sm shadow-xl w-full"
                />
                <div className="absolute -bottom-6 -left-6 bg-[#D32F2F] text-white p-6 rounded-sm hidden md:block">
                  <p className="text-3xl font-bold font-barlow">19+</p>
                  <p className="text-sm">Jahre Erfahrung</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact Box */}
      {contactInfo && (
        <section className="section-padding bg-[#0A2A43]" data-testid="contact-box-section">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 font-barlow">
                  Haben Sie ein Projekt?
                </h2>
                <p className="text-white/80 mb-8">
                  Kontaktieren Sie uns für eine kostenlose Beratung. Wir freuen uns auf Ihr Bauvorhaben!
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D32F2F] rounded-sm flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold">{contactInfo.street}</p>
                      <p className="text-white/70">{contactInfo.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D32F2F] rounded-sm flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold">{contactInfo.phone}</p>
                      <p className="text-white/70">{contactInfo.mobile}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D32F2F] rounded-sm flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold">{contactInfo.email}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 rounded-sm shadow-xl">
                <h3 className="text-xl font-bold text-[#0A2A43] mb-6 font-barlow">
                  Schnellanfrage
                </h3>
                <QuickContactForm />
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

// Quick Contact Form Component
const QuickContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "Allgemeine Anfrage",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", subject: "Allgemeine Anfrage", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h4 className="text-lg font-bold text-[#0A2A43] mb-2">Vielen Dank!</h4>
        <p className="text-[#475569]">Wir werden uns schnellstmöglich bei Ihnen melden.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="quick-contact-form">
      <input
        type="text"
        placeholder="Ihr Name *"
        required
        className="form-input"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        data-testid="quick-form-name"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="email"
          placeholder="E-Mail *"
          required
          className="form-input"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          data-testid="quick-form-email"
        />
        <input
          type="tel"
          placeholder="Telefon"
          className="form-input"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          data-testid="quick-form-phone"
        />
      </div>
      <textarea
        placeholder="Ihre Nachricht *"
        required
        rows={4}
        className="form-input resize-none"
        value={formData.message}
        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
        data-testid="quick-form-message"
      />
      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full disabled:opacity-50"
        data-testid="quick-form-submit"
      >
        {isSubmitting ? "Wird gesendet..." : "Nachricht senden"}
      </button>
    </form>
  );
};

export default HomePage;
